/**
 * Entry point of the Vue application.
 * This file initializes Vue, integrates Sentry for error tracking and performance monitoring,
 * and mounts the main App component to the DOM.
 */

/**
 * Import Vue and necessary plugins.
 * @module Vue
 */
/* eslint-disable import/no-extraneous-dependencies */
import Vue from 'vue';
import Vuelidate from 'vuelidate';
import App from './App.vue';
import router from './router';
import store from './store';
import vuetify from './plugins/vuetify';
import '@mdi/font/css/materialdesignicons.css';
import 'typeface-roboto/index.css';
import initSentry from './plugins/sentry';
import initHttp from './plugins/http';
import initAxiosHeaders from './plugins/axiosHeaders';

/**
 * Enable Vuelidate plugin for form validation.
 */
Vue.use(Vuelidate);

/**
 * Disable production tip in Vue.
 * @type {boolean}
 */
Vue.config.productionTip = false;

initSentry(Vue);

/**
 * Connect the shared HTTP layer to the store, covering the CSRF token, the
 * Accept-Language header, and the global handling of 401 and 403 responses.
 */
initHttp(store);

/**
 * Add the identity headers that the legacy axios transport cannot declare in
 * one place, because each store module builds its own header object.
 */
initAxiosHeaders();

/**
 * Create and mount the Vue instance.
 * @param {Object} router - Vue Router instance.
 * @param {Object} store - Vuex store instance.
 * @param {Object} vuetify - Vuetify instance for UI components.
 * @param {Function} render - Render function for the main App component.
 */
new Vue({
  router,
  store,
  vuetify,
  render: (h) => h(App),
}).$mount('#app');
